import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { Genre as GenreType } from '../../types/Genre';
import { Box, Button, Modal, Stack, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCancel, faPenToSquare, faPlay, faTrash } from "@fortawesome/free-solid-svg-icons";
import { deleteGenre, getArtistsByGenre, getGenre } from "../../components/apis/music-api";
import { format, getLetter } from "../../components/text-helper/text-helper";
import { ArtistMessage } from "../../types/ArtistMessage";
import callApi from "../../components/call-api/call-api";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Corners, getOuterCorners } from "../../components/coordinate-helper/coordinate-helper";
import { PlayModes } from "../../types/PlayModes";
import { useMusicStore } from "../../components/store/store";
import "./Genre.scss"

type GenreProps = {
    id: string,
}

const Genre = () => {
    const params = useParams<GenreProps>();
    const navigate = useNavigate();
    const { getAccessToken } = useContext(AuthenticationContext);
    const { setGenreId, setPlayMode, setGenreName } = useMusicStore();
    const [ genre, setGenre ] = useState<GenreType>();
    const [ artistMessages, setArtistMessages ] = useState<ArtistMessage[]>();
    const [ open, setOpen ] = useState(false);
    const GoolgleApiKey = 'AIzaSyDSwvr2YTLE9ybiYn_15b1YLxcPqU5TDm0';
    
    const [ mapContainer, setMapContainer ] = useState<HTMLDivElement | null>(null);
    const [ corners, setCorners ] = useState<Corners>();
    const [ map, setMap ] = useState<google.maps.Map>();
    const [ markerCluster, setMarkerClusters ] = useState<MarkerClusterer>();

    const handleMapRef = useCallback((node: HTMLDivElement | null) => {
        setMapContainer(node);
    }, []);
    
    useEffect(() => {
        callApi<GenreType>(getGenre(getAccessToken(), params.id ?? ''), setGenre);
        callApi<ArtistMessage[]>(getArtistsByGenre(getAccessToken(), params.id ?? ''), setArtistMessages);
    }, [params, getAccessToken]);

    useEffect(()=>{
        if (mapContainer && !map && window.google?.maps) {
            setMap(new window.google.maps.Map(mapContainer, {
                zoom: 2,
                center: {lat: 0, lng: 0},
            }));
        }
    }, [mapContainer, map]);

    useEffect(() => {
        if (artistMessages && artistMessages.length > 0) {
            setCorners(getOuterCorners(artistMessages));
        }
    }, [artistMessages]);

    useEffect(()=> {
        if (markerCluster) {
            markerCluster.clearMarkers();
            artistMessages?.forEach(artistMessage => {
                if (artistMessage.coordinates != null) {
                    var position = { 
                        position: {
                            lat: artistMessage.coordinates?.latitude ?? 0,
                            lng: artistMessage.coordinates?.longitude ?? 0
                        },
                        title: format(artistMessage.name),
                    };
                    
                    var marker = new window.google.maps.Marker(position);
                    markerCluster.addMarker(marker);
                }
            });
        }
    }, [markerCluster, artistMessages]);

    useEffect(() => {
        if (map && !markerCluster) {
            setMarkerClusters(new MarkerClusterer({map, markers: [], }));
        }
            
        if (map && corners) {
            const southwest = new google.maps.LatLng(corners.southwest.latitude, corners.southwest.longitude);
            const northeast = new google.maps.LatLng(corners.northeast.latitude, corners.northeast.longitude);
            const bounds = new google.maps.LatLngBounds(southwest, northeast);
            google.maps.event.trigger(map, 'resize');
            map.fitBounds(bounds);
            map.panToBounds(bounds);
        }
    }, [map, markerCluster, corners]);

    const deleteThis = async () => {
        if (genre !== undefined) {
            await deleteGenre(getAccessToken(), genre.id);
            navigate(`/genres`);
        }
    }

    const handleDelete = () => {
        deleteThis();
    }
    
    const setJukeBox = () => {
        if (genre !== undefined) {
            setGenreId(genre.id);
            setGenreName(genre.name);
            setPlayMode(PlayModes.Genre);
        }
    }

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '1px solid #000',
        borderRadius: '12px',
        boxShadow: 24,
        p: 4,
    };
    
    const render = (status: Status) => (<h1>{status}</h1>)

    return (
        <>
            <Modal open={open} onClose={handleDelete}>
                <Box sx={style}>
                    <Stack spacing={1} direction="column">
                        <Box>
                            <Typography id="modal-modal-title" variant="h6" component="h2">
                                Delete {genre?.name}?
                            </Typography>
                        </Box>
                        <Box>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Are you sure that you want to delete this genre and it's artist relations.
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Button variant="contained" size="small" onClick={() => setOpen(false)} color="error">
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faCancel} className="icon" />
                                Cancel
                            </Button>
                            <Button variant="contained" size="small" onClick={handleDelete} color="warning">
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faTrash} className="icon" />
                                Delete
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>
            <ButtonBar
                leftButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/Genres`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon" />
                        Back To Genres
                    </Button>,
                    <Button component={Link} variant="contained" size="small" to={`#`} onClick={setJukeBox}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon" />
                        Play
                    </Button>
                ]}
                rightButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/Genres/${params.id}/edit`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon" />
                        Edit Genre
                    </Button>,
                    <Button variant="contained" size="small" onClick={() => setOpen(true)}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faTrash} className="icon" />
                        Delete Genre
                    </Button>
                ]}
            />
            <Stack>
                <Box>
                    <Typography variant='h5'>{genre?.name ?? ''}</Typography>
                </Box>
                <Box>
                    <Typography style={{ whiteSpace: "pre-wrap", textAlign: 'left', fontSize: '14px' }}>{genre?.description}</Typography>
                </Box>
                <Box sx={{ pt: '12px' }}>
                    <Typography variant='h6'>{artistMessages?.length} artists</Typography>
                </Box>
            </Stack>
            <Stack>
                <Box>                    
                    <div style={{ height: '60vh', width: '100%', paddingBottom: '24px' }}>
                        <Wrapper apiKey={GoolgleApiKey} render={render}>
                            <div ref={handleMapRef} style={{height: "100%", width: "100%"}} ></div>
                        </Wrapper>
                    </div>
                </Box>
            </Stack>
            {artistMessages?.map((artist: ArtistMessage, index: number) =>
                <Box component={Link}
                    sx={{ 
                        width: '360px',
                        display: 'inline-flex',
                        p: '6px',
                        m: '2px',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        backgroundColor: artist.backgroundColor,
                        color: artist.color === null || artist.color === "" ? '#666666' : artist.color ,
                    }}
                    key={`$artist-${index}`}
                    to={`/letters/${getLetter(artist.name)}/artists/${artist.name}`}
                >
                    {format(artist.name)}
                </Box>
            )}
        </>
    )
}

export default Genre;